import React from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import Image from "../../Image";
import LinkComponent, { Link } from "../../LinkComponent";
import Button from "../../NewForms/Button";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { replaceMarkdownBreak, SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";
import ShapeImg from "./ShapeImg";
import SmallShapeImg from "./SmallShape";

const Container = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    ${({ theme }) =>
        theme.breakpoints.from.medium.css`
            flex-direction: row;
            height: 50vh;
      `}
`;

const HeadlineContainer = styled.div`
    position: relative;
    z-index: 2;

    ${({ theme }) =>
        theme.breakpoints.from.medium.css`
            position: absolute;
            max-width: 60%;
            padding: 20px 0;
      `}
`;

const ImgContainer = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: 300px;
    ${({ theme }) =>
        theme.breakpoints.from.medium.css`
          position: absolute;
          max-width: 80%;
          height: 100%;
      `}
    ${({ theme }) => theme.breakpoints.from.large.css`
        max-width: 60%;
    `}

    .gatsby-image-wrapper {
        width: 100%;
    }
`;

const ImgFilter = styled.div`
    position: absolute;
    z-index: 1;
    top: 0;
    ${({ theme }) => theme.breakpoints.upTo.medium.css`
        top: -1px;
    `}

    left: 0;
    bottom: -1px;

    svg {
        position: relative;
        width: auto;
        height: 100%;

        /* Safari fix: */
        ${({ theme }) => theme.breakpoints.from.extraLarge.css`
            min-height: 525px;
        `}
    }
`;

const NonCmsImgDummy = styled.div`
    width: 100%;
    height: 100%;
`;

const StyledImgFilter = styled(ImgFilter)`
    display: none;

    ${({ theme }) => theme.breakpoints.from.medium.css`
        display: block;
    `}
`;

const CtaContainer = styled.div`
    display: flex;
    padding: 0 20px 20px;
    ${({ theme }) => css`
        ${theme.paddings.containerOuter.left}
        ${theme.paddings.containerOuter.right}
        ${theme.paddings.containerOuter.bottom}
    `}

    ${({ theme }) => theme.breakpoints.from.small.css`
        display: inline-flex;
    `};

    ${({ theme }) => theme.breakpoints.from.medium.css`
        padding: 0;
    `};
`;

const ConditionalLink = ({ cta, children }) =>
    cta?.href ? <Link to={cta.href}>{children}</Link> : <div>{children}</div>;

const ImgShapeContainer = ({
    text,
    image,
    link,
    imgPositionX = "center",
    imgPositionY = "center",
    isSmallShape = false,
    hasTextOutline = true,
    side = "right",
    headlineSize = "4.5em",
    headlineNumber,
    gtag = undefined,
    imageMirroring = true,
    nonCmsImageSrc = undefined,
    cta = undefined,
    ...props
}) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const styledImgContainer = css`
        width: 100%;
        ${side === "left" ? "left: 0; order: 0;" : "right: 0;"};
        ${side === "left" && imageMirroring ? "transform: scaleX(-1);" : ""}
        ${theme.breakpoints.from.medium.css`
            ${side === "left" ? "max-width: 55%;" : "max-width: 70%;"}`}
        ${theme.breakpoints.from.medium.css`
            ${side === "left" ? "max-width: 55%;" : ""}`}
        ${theme.breakpoints.upTo.small.css`
            ${side === "left" && "order: 1"}`}
    `;

    const styledHeadlineContainer = css`
        ${side === "left"
            ? css`
                  right: 0;
                  order: 1;
              `
            : css`
                  left: 0;
              `};

        padding: 0;

        ${theme.paddings.containerOuter.right}
        ${theme.paddings.containerOuter.left}

        ${theme.breakpoints.from.medium.css`
            padding: 20px 0;
            ${
                side === "left"
                    ? theme.paddings.containerOuter.right
                    : theme.paddings.containerOuter.left
            }
        `}
        ${side === "left" &&
        theme.breakpoints.upTo.small.css`
                order: 0
        `}
    `;

    const headlineCss = css`
        font-weight: 900;
        color: ${theme.colors.red.toString()};
        word-wrap: normal;
        ${side === "left" && "text-align: right;"}
        ${theme.breakpoints.upTo.small.css`
            line-height: 2.75rem;

            br {
              display: none;
            }
            ${side === "left" && "text-align: left;"}
            ${theme.paddings.containerOuter.left}
        `}
        ${theme.breakpoints.upTo.extraSmall.css`
            line-height: 2.5rem;
        `}
    `;

    const LeftHeadline = css`
        right: 0;
        ${theme.paddings.containerOuter.right}

        text-align: right;
    `;

    const styledImgFilter = css`
        ${side === "left" && "left: -1px;"}
        ${side === "left" && !imageMirroring
            ? "transform: scaleX(-1); right: -1px;"
            : ""}
    `;

    return (
        <Container {...props} id="element-img-shape">
            <HeadlineContainer css={styledHeadlineContainer}>
                <SideNavContainer paddingLeft={paddingLeft}>
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                        `}
                    >
                        {text && (
                            <>
                                {theme.breakpoints.upTo.medium.useMatches() && (
                                    <HeadlineItem
                                        css={headlineCss}
                                        headline={headlineNumber}
                                        fontSize={headlineSize}
                                        fSMedium="3rem"
                                        fSNormal="3.25rem"
                                        fSSmall="2.5rem"
                                        fSExtraSmall="2rem"
                                        hasTextOutline={hasTextOutline}
                                    >
                                        {replaceMarkdownBreak(text)}
                                    </HeadlineItem>
                                )}

                                {theme.breakpoints.from.normal.useMatches() && (
                                    <HeadlineItem
                                        css={headlineCss}
                                        headline={headlineNumber}
                                        fontSize={headlineSize}
                                        fSMedium="3rem"
                                        fSNormal="3.25rem"
                                        fSSmall="2.5rem"
                                        fSExtraSmall="2rem"
                                        hasTextOutline={hasTextOutline}
                                    >
                                        {text}
                                    </HeadlineItem>
                                )}
                            </>
                        )}
                        {link && (
                            <LinkComponent
                                icon="arrow-right"
                                css={css`
                                    ${LeftHeadline}
                                    ${side === "left" &&
                                    "justify-content: flex-end; padding-right: 0 !important;"}
                                `}
                                link={link}
                                {...(gtag ? { "data-gtag": gtag } : {})}
                            />
                        )}
                        {cta && cta.href && (
                            <CtaContainer>
                                <Button to={cta.href}>{cta.text}</Button>
                            </CtaContainer>
                        )}
                    </div>
                </SideNavContainer>
            </HeadlineContainer>
            <ConditionalLink cta={cta}>
                <ImgContainer
                    css={css`
                        ${styledImgContainer};

                        background-repeat: no-repeat;
                        background-position-x: ${imgPositionX};
                        background-position-y: ${imgPositionY};
                        background-size: cover;

                        img {
                            object-fit: cover;
                            object-position: ${imgPositionX} ${imgPositionY};
                        }

                        ${nonCmsImageSrc &&
                        `background-image: url("${nonCmsImageSrc}")`}
                    `}
                >
                    {nonCmsImageSrc ? <NonCmsImgDummy /> : <Image {...image} />}

                    <StyledImgFilter css={styledImgFilter}>
                        {isSmallShape ? <SmallShapeImg /> : <ShapeImg />}
                    </StyledImgFilter>
                </ImgContainer>
            </ConditionalLink>
        </Container>
    );
};

export const imgShapeEmotionStyled = css`
    margin: 20px 0;
`;

export default ImgShapeContainer;
